import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { Loader2, Check, ArrowUpRight } from "lucide-react";

const SERVICES = ["Physiotherapy", "Fitness", "Rehab", "Foot Spa", "Cupping"];
const TIME_SLOTS = [
  "10:00","10:30","11:00","11:30","12:00","12:30",
  "13:00","13:30","14:00","14:30","15:00","15:30",
  "16:00","16:30","17:00","17:30","18:00","18:30",
];

export default function Book() {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [date, setDate] = useState(null);
  const [service, setService] = useState("Physiotherapy");
  const [slot, setSlot] = useState(null);
  const [booked, setBooked] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [success, setSuccess] = useState(null);

  const dateStr = date ? format(date, "yyyy-MM-dd") : null;

  useEffect(() => {
    if (!dateStr) return;
    setLoadingSlots(true);
    setSlot(null);
    api.get(`/appointments/availability`, { params: { date: dateStr } })
      .then((r) => setBooked(r.data.booked || []))
      .catch(() => setBooked([]))
      .finally(() => setLoadingSlots(false));
  }, [dateStr]);

  const isFriday = false;

  const disableDate = (d) => {
    if (!d) return true;
    const n = new Date(d); n.setHours(0,0,0,0);
    if (n < today) return true;
    // 3 months window
    const max = new Date(today); max.setMonth(max.getMonth() + 3);
    if (n > max) return true;
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please pick a date");
    if (!slot) return toast.error("Please pick a time slot");
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill in your name, email and phone");

    setSubmitting(true);
    try {
      const payload = { ...form, service, appointment_date: dateStr, appointment_time: slot };
      const { data } = await api.post("/appointments", payload);
      setSuccess(data);
      toast.success("Booking request received!", { description: `We will confirm your ${service} session shortly.` });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to book. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main data-testid="booking-success" className="min-h-[70vh] flex items-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 w-full">
          <div className="border border-stone-200 bg-white p-10 sm:p-14">
            <div className="w-12 h-12 rounded-full bg-emerald-900 text-stone-50 flex items-center justify-center">
              <Check size={22} />
            </div>
            <div className="eyebrow mt-8">Booking received</div>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-light text-stone-900">See you soon, {success.name.split(" ")[0]}.</h1>
            <p className="mt-6 text-stone-600 leading-relaxed">
              We've received your <b>{success.service}</b> request for <b>{format(new Date(success.appointment_date), "EEEE, d MMM yyyy")}</b> at <b>{success.appointment_time}</b>. Our team will call you at <b>{success.phone}</b> to confirm.
            </p>
            <div className="mt-10 divider-dashed" />
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/" data-testid="success-home" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-stone-300 text-stone-800 hover:border-emerald-900 hover:text-emerald-900 transition-colors duration-200">Back to home</Link>
              <button
                data-testid="success-new"
                onClick={() => { setSuccess(null); setDate(null); setSlot(null); setForm({ name:"", email:"", phone:"", notes:"" }); }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-stone-50 hover:bg-emerald-950 transition-colors duration-200"
              >
                Book another <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="book-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="eyebrow">Book</div>
      <h1 className="mt-3 font-heading text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">Reserve your slot.</h1>
      <p className="mt-4 text-stone-600 max-w-2xl">Pick a date, choose a time, and tell us who you are. We'll confirm by phone or email shortly.</p>

      <form onSubmit={submit} className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* LEFT: Date + Time */}
        <div className="lg:col-span-7 space-y-8">
          <div className="border border-stone-200 bg-white p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="eyebrow">Step 1</div>
                <h2 className="mt-1 font-heading text-2xl text-stone-900">Choose a date</h2>
              </div>
              {date && <div className="text-sm text-stone-600" data-testid="selected-date">{format(date, "EEEE, d MMM yyyy")}</div>}
            </div>
            <div className="inline-block border border-stone-200 rounded-md p-2 bg-stone-50" data-testid="date-picker">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={disableDate}
                initialFocus
              />
            </div>
            <p className="mt-3 text-xs text-stone-500">Open 7 days a week, 10 AM – 7 PM. Bookings open up to 3 months in advance.</p>
          </div>

          <div className="border border-stone-200 bg-white p-6 sm:p-8">
            <div className="eyebrow">Step 2</div>
            <h2 className="mt-1 font-heading text-2xl text-stone-900">Pick a time</h2>
            {!date && <p className="mt-4 text-stone-500">Select a date first.</p>}
            {date && loadingSlots && (
              <div className="mt-4 flex items-center gap-2 text-stone-500"><Loader2 className="animate-spin" size={16} /> Checking availability...</div>
            )}
            {date && !loadingSlots && (
              <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="time-slots">
                {TIME_SLOTS.map((t) => {
                  const isBooked = booked.includes(t);
                  const isActive = slot === t;
                  return (
                    <button
                      type="button"
                      key={t}
                      disabled={isBooked}
                      data-testid={`time-slot-${t}`}
                      onClick={() => setSlot(t)}
                      className={`px-2 py-2 text-sm border transition-colors duration-200 font-mono ${
                        isBooked
                          ? "bg-stone-100 border-stone-200 text-stone-400 line-through cursor-not-allowed"
                          : isActive
                          ? "bg-emerald-900 border-emerald-900 text-stone-50"
                          : "bg-white border-stone-200 text-stone-700 hover:border-emerald-900 hover:text-emerald-900"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Details */}
        <div className="lg:col-span-5">
          <div className="border border-stone-200 bg-white p-6 sm:p-8 sticky top-24">
            <div className="eyebrow">Step 3</div>
            <h2 className="mt-1 font-heading text-2xl text-stone-900">Your details</h2>

            <div className="mt-6 space-y-4">
              <Field label="Service">
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="service-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/\s+/g,"-").toLowerCase()}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Full name">
                <input data-testid="name-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 text-stone-800 bg-white" placeholder="e.g. Ravi Kumar" />
              </Field>

              <Field label="Email">
                <input type="email" data-testid="email-input" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 text-stone-800 bg-white" placeholder="you@example.com" />
              </Field>

              <Field label="Phone">
                <input data-testid="phone-input" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 text-stone-800 bg-white" placeholder="+91 ..." />
              </Field>

              <Field label="Notes (optional)">
                <textarea data-testid="notes-input" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 text-stone-800 bg-white min-h-[88px]" placeholder="Any injury, area of concern or preference..." />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-booking"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-900 text-stone-50 font-medium hover:bg-emerald-950 disabled:opacity-60 transition-colors duration-200"
            >
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Booking...</> : <>Confirm booking <ArrowUpRight size={16} /></>}
            </button>
            <p className="mt-3 text-xs text-stone-500 text-center">You'll receive a confirmation email once approved.</p>
          </div>
        </div>
      </form>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
